import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { loadUser } from '../../features/auth/authSlice';
import { dashboardService } from '../../services/dashboardService';
import { ANNUAL_REVENUE_BANDS } from '../../constants/annualRevenueBands';
import { formatCpfCnpjInput, isValidCNPJ, isValidCPF, cleanDocument } from '../../utils/cpfCnpjValidator';
import { applyPhoneMask } from '../../utils/phoneValidation';
import { formatCepInput, lookupViaCep, onlyDigitsCep } from '../../utils/viaCep';
import type { PublicRecipientProfilePayload } from '../../services/parishService';

function maskDdMmYyyy(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

const AddOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const allowed = user?.userType === 'parish_admin' && user?.allowAdditionalOrganizations === true;

  const [submitting, setSubmitting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [phone, setPhone] = useState('');
  const [signerCpf, setSignerCpf] = useState('');
  const [parishData, setParishData] = useState({
    name: '',
    address: '',
    streetNumber: '',
    neighborhood: '',
    ibgeMunicipioCode: '',
    city: '',
    state: '',
    zipCode: '',
    parishEmail: '',
    description: '',
    cnpj: '',
  });
  const [recipientProfile, setRecipientProfile] = useState({
    companyName: '',
    tradingName: '',
    annualRevenue: '',
    foundingDate: '',
    repBirthdate: '',
    repPhone: '',
    holderName: '',
    bank: '',
    branchNumber: '',
    branchCheckDigit: '',
    accountNumber: '',
    accountCheckDigit: '',
    accountType: 'checking' as 'checking' | 'savings',
  });

  useEffect(() => {
    if (!allowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [allowed, navigate]);

  useEffect(() => {
    const n = parishData.name.trim();
    if (!n) return;
    setRecipientProfile((prev) => {
      let changed = false;
      const next = { ...prev };
      if (!prev.companyName.trim()) {
        next.companyName = n;
        changed = true;
      }
      if (!prev.tradingName.trim()) {
        next.tradingName = n;
        changed = true;
      }
      if (!prev.holderName.trim()) {
        next.holderName = n.slice(0, 30);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [parishData.name]);

  const holderDocumentFromParishCnpj = useMemo(() => {
    const d = cleanDocument(parishData.cnpj);
    if (d.length === 14) return formatCpfCnpjInput(d);
    return '';
  }, [parishData.cnpj]);

  const fillAddressFromCep = async (zip: string) => {
    const digits = onlyDigitsCep(zip);
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const found = await lookupViaCep(digits);
      if (!found) return;
      setParishData((prev) => ({
        ...prev,
        zipCode: formatCepInput(digits),
        address: found.logradouro || prev.address,
        neighborhood: found.bairro || prev.neighborhood,
        city: found.localidade || prev.city,
        state: found.uf || prev.state,
        ibgeMunicipioCode: found.ibge || prev.ibgeMunicipioCode,
      }));
    } finally {
      setCepLoading(false);
    }
  };

  const buildRecipientProfile = (): PublicRecipientProfilePayload => {
    const rev = recipientProfile.annualRevenue.replace(/\D/g, '');
    const repPhoneCombined = recipientProfile.repPhone.trim() || phone.trim();
    const repPhoneMasked = applyPhoneMask(repPhoneCombined);
    const acctDigit = String(recipientProfile.accountCheckDigit || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2);
    return {
      registerType: 'corporation',
      corporation: {
        companyName: recipientProfile.companyName.trim(),
        tradingName: recipientProfile.tradingName.trim(),
        annualRevenue: rev.length >= 1 ? rev : '500000',
        ...(recipientProfile.foundingDate.trim()
          ? { foundingDate: recipientProfile.foundingDate.trim() }
          : {}),
        representative: {
          name: (user?.name || '').trim(),
          email: (user?.email || '').trim().toLowerCase(),
          document: cleanDocument(signerCpf),
          birthdate: recipientProfile.repBirthdate.trim(),
          ...(repPhoneMasked || repPhoneCombined
            ? { phone: repPhoneMasked || repPhoneCombined }
            : {}),
          selfDeclaredRepresentative: true,
        },
      },
      bankAccount: {
        holderName: recipientProfile.holderName.trim(),
        holderDocument: formatCpfCnpjInput(cleanDocument(parishData.cnpj)).trim(),
        bank: recipientProfile.bank.replace(/\D/g, '').slice(0, 4),
        branchNumber: recipientProfile.branchNumber.replace(/\D/g, ''),
        branchCheckDigit:
          recipientProfile.branchCheckDigit.replace(/\D/g, '').slice(0, 2) || undefined,
        accountNumber: recipientProfile.accountNumber.replace(/\D/g, ''),
        accountCheckDigit: acctDigit,
        type: recipientProfile.accountType,
      },
    };
  };

  const validate = (): string | null => {
    if (!parishData.name.trim()) return 'Informe o nome da organização.';
    if (!isValidCNPJ(parishData.cnpj)) return 'CNPJ inválido.';
    if (onlyDigitsCep(parishData.zipCode).length !== 8) return 'CEP inválido.';
    if (!parishData.address.trim()) return 'Informe o logradouro.';
    if (!parishData.streetNumber.trim()) return 'Informe o número.';
    if (!parishData.neighborhood.trim()) return 'Informe o bairro.';
    if (!parishData.city.trim()) return 'Informe a cidade.';
    if (!parishData.state.trim() || parishData.state.trim().length !== 2) return 'UF inválida.';
    if (!phone.trim()) return 'Informe o telefone da organização.';
    if (!isValidCPF(signerCpf)) return 'CPF do representante inválido.';
    if (!recipientProfile.repBirthdate.trim() || recipientProfile.repBirthdate.length < 10) {
      return 'Informe a data de nascimento do representante.';
    }
    if (!recipientProfile.companyName.trim()) return 'Informe a razão social.';
    if (!recipientProfile.tradingName.trim()) return 'Informe o nome fantasia.';
    if (!recipientProfile.annualRevenue) return 'Selecione o faturamento anual.';
    if (!recipientProfile.holderName.trim()) return 'Informe o titular da conta.';
    if (!recipientProfile.bank.trim()) return 'Informe o banco.';
    if (!recipientProfile.branchNumber.trim()) return 'Informe a agência.';
    if (!recipientProfile.accountNumber.trim()) return 'Informe a conta.';
    if (!recipientProfile.accountCheckDigit.trim()) return 'Informe o dígito da conta.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const recipient = buildRecipientProfile();
      const res = await dashboardService.createAdditionalOrganization({
        phone,
        parishData: {
          ...parishData,
          cnpj: cleanDocument(parishData.cnpj),
        },
        recipientProfile: recipient,
      });
      setSuccess(res?.message || 'Organização criada. Use o seletor para alternar.');
      await dispatch(loadUser());
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível criar a organização.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowed) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Adicionar organização
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Cadastre um novo CNPJ na mesma conta. A organização atual permanece ativa; troque pelo seletor
        no menu lateral.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Organização
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nome da organização"
              required
              fullWidth
              value={parishData.name}
              onChange={(e) => setParishData((p) => ({ ...p, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="CNPJ"
              required
              fullWidth
              value={parishData.cnpj}
              onChange={(e) =>
                setParishData((p) => ({
                  ...p,
                  cnpj: formatCpfCnpjInput(e.target.value.replace(/\D/g, '').slice(0, 14)),
                }))
              }
              inputProps={{ maxLength: 18 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefone"
              required
              fullWidth
              value={phone}
              onChange={(e) => setPhone(applyPhoneMask(e.target.value))}
              inputProps={{ maxLength: 15 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="E-mail institucional"
              fullWidth
              type="email"
              value={parishData.parishEmail}
              onChange={(e) => setParishData((p) => ({ ...p, parishEmail: e.target.value }))}
              helperText="Opcional — usa seu e-mail de login se vazio"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="CEP"
              required
              fullWidth
              value={parishData.zipCode}
              onChange={(e) =>
                setParishData((p) => ({ ...p, zipCode: formatCepInput(e.target.value) }))
              }
              onBlur={() => void fillAddressFromCep(parishData.zipCode)}
              InputProps={{
                endAdornment: cepLoading ? <CircularProgress size={18} /> : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Logradouro"
              required
              fullWidth
              value={parishData.address}
              onChange={(e) => setParishData((p) => ({ ...p, address: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Número"
              required
              fullWidth
              value={parishData.streetNumber}
              onChange={(e) => setParishData((p) => ({ ...p, streetNumber: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Bairro"
              required
              fullWidth
              value={parishData.neighborhood}
              onChange={(e) => setParishData((p) => ({ ...p, neighborhood: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Cidade"
              required
              fullWidth
              value={parishData.city}
              onChange={(e) => setParishData((p) => ({ ...p, city: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="UF"
              required
              fullWidth
              value={parishData.state}
              onChange={(e) =>
                setParishData((p) => ({
                  ...p,
                  state: e.target.value.toUpperCase().slice(0, 2),
                }))
              }
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="IBGE"
              fullWidth
              value={parishData.ibgeMunicipioCode}
              onChange={(e) =>
                setParishData((p) => ({
                  ...p,
                  ibgeMunicipioCode: e.target.value.replace(/\D/g, '').slice(0, 7),
                }))
              }
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Empresa (PJ) e representante
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Razão social"
              required
              fullWidth
              value={recipientProfile.companyName}
              onChange={(e) =>
                setRecipientProfile((p) => ({ ...p, companyName: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome fantasia"
              required
              fullWidth
              value={recipientProfile.tradingName}
              onChange={(e) =>
                setRecipientProfile((p) => ({ ...p, tradingName: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="add-org-revenue">Faturamento anual estimado</InputLabel>
              <Select
                labelId="add-org-revenue"
                label="Faturamento anual estimado"
                value={recipientProfile.annualRevenue}
                onChange={(e) =>
                  setRecipientProfile((p) => ({
                    ...p,
                    annualRevenue: String(e.target.value),
                  }))
                }
              >
                {ANNUAL_REVENUE_BANDS.map((b) => (
                  <MenuItem key={b.valueCentavos} value={b.valueCentavos}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Estimativa usada no cadastro do recebedor</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Data de fundação (opcional)"
              fullWidth
              placeholder="DD/MM/AAAA"
              value={recipientProfile.foundingDate}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  foundingDate: maskDdMmYyyy(e.target.value),
                }))
              }
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="CPF do representante"
              required
              fullWidth
              value={signerCpf}
              onChange={(e) =>
                setSignerCpf(formatCpfCnpjInput(e.target.value.replace(/\D/g, '').slice(0, 11)))
              }
              helperText={`Representante: ${user?.name || '—'}`}
              inputProps={{ maxLength: 14 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nascimento do representante"
              required
              fullWidth
              placeholder="DD/MM/AAAA"
              value={recipientProfile.repBirthdate}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  repBirthdate: maskDdMmYyyy(e.target.value),
                }))
              }
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Conta para recebimentos
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Titular da conta"
              required
              fullWidth
              value={recipientProfile.holderName}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  holderName: e.target.value.slice(0, 30),
                }))
              }
              inputProps={{ maxLength: 30 }}
              helperText={`${recipientProfile.holderName.length}/30`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="CNPJ do titular"
              required
              fullWidth
              value={holderDocumentFromParishCnpj}
              InputProps={{ readOnly: true }}
              helperText="Igual ao CNPJ da organização"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Banco"
              required
              fullWidth
              value={recipientProfile.bank}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  bank: e.target.value.replace(/\D/g, '').slice(0, 4),
                }))
              }
              inputProps={{ maxLength: 4 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Agência"
              required
              fullWidth
              value={recipientProfile.branchNumber}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  branchNumber: e.target.value.replace(/\D/g, ''),
                }))
              }
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Díg. agência"
              fullWidth
              value={recipientProfile.branchCheckDigit}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  branchCheckDigit: e.target.value.replace(/\D/g, '').slice(0, 2),
                }))
              }
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Conta"
              required
              fullWidth
              value={recipientProfile.accountNumber}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  accountNumber: e.target.value.replace(/\D/g, ''),
                }))
              }
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Dígito"
              required
              fullWidth
              value={recipientProfile.accountCheckDigit}
              onChange={(e) =>
                setRecipientProfile((p) => ({
                  ...p,
                  accountCheckDigit: e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2),
                }))
              }
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel id="add-org-acct-type">Tipo</InputLabel>
              <Select
                labelId="add-org-acct-type"
                label="Tipo"
                value={recipientProfile.accountType}
                onChange={(e) =>
                  setRecipientProfile((p) => ({
                    ...p,
                    accountType: e.target.value as 'checking' | 'savings',
                  }))
                }
              >
                <MenuItem value="checking">Corrente</MenuItem>
                <MenuItem value="savings">Poupança</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard')} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Criar organização'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AddOrganizationPage;
